<template>
  <section :id="module.id" class="hero-section" :style="heroElStyles">
    <GlobalUIMedia
      v-if="module.config.general.showMedia"
      :media="module.config.general.media"
    />

    <div class="inner-wrapper">
      <GlobalUIMedia
        v-if="module.config.media.media.url"
        :media="module.config.media"
        placement="front"
      />

      <div class="content">
        <div class="titles">
          <h1 v-if="jobData?.title" class="title">
            {{ jobData.title }}
          </h1>
          <div
            v-if="module.config.general.showSubtitle && jobData?.sub_title"
            class="sub-title"
          >
            {{ jobData.sub_title }}
          </div>
        </div>

        <div v-if="resolvedJobDetails.length > 0" class="job-details">
          <JobDetailPill
            v-for="jobDetail in resolvedJobDetails"
            :key="jobDetail.key"
            :label="jobDetail.label"
            :value="jobDetail.value"
            :text-color="module.config.jobDetails.textColor.value"
            :background-color="module.config.jobDetails.backgroundColor.value"
          />
        </div>

        <div class="button-area">
          <GlobalUIApplyButton
            v-if="module.config.applyButton"
            :button="module.config.applyButton"
            :application-url="jobData.application_url"
            @emitted-click="$emit('emittedClick')"
          />

          <GlobalUIApplyButton
            v-if="pitchYouButton"
            :button="pitchYouButton"
            :application-url="jobData.pitch_you_url"
            @emitted-click="$emit('emittedClick')"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import heroModule from "./heroDefaultModuleProp";
import GlobalUIMedia from "../GlobalUI/GlobalUIMedia.vue";
import JobDetailPill from "./UI/JobDetailPill.vue";
import GlobalUIApplyButton from "../GlobalUI/GlobalUIApplyButton/GlobalUIApplyButton.vue";
import { pitchYouButtonMixin } from "../../utils/mixins/pitchYouButtonMixin.js";

export default {
  name: "HeroModule",
  components: {
    GlobalUIMedia,
    JobDetailPill,
    GlobalUIApplyButton,
  },
  mixins: [pitchYouButtonMixin],
  props: {
    module: {
      type: Object,
      required: true,
      default() {
        return heroModule;
      },
    },

    jobData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["emittedClick"],
  computed: {
    heroElStyles() {
      const { backgroundColor } = this.module.config.general;
      const headerTextColor = this.module.config.header.textColor;
      const headerCapitalLetters = this.module.config.header.isTextInCaps;
      const subtitleTextColor = this.module.config.subtitle.textColor;
      const subtitleCapitalLetters = this.module.config.subtitle.isTextInCaps;

      return {
        "--hero-background-color": backgroundColor.value,
        "--hero-header-text-color": headerTextColor.value,
        "--hero-sub-title-text-color": subtitleTextColor.value,
        "--hero-header-capital-letters": headerCapitalLetters
          ? "uppercase"
          : "initial",
        "--hero-sub-title-capital-letters": subtitleCapitalLetters
          ? "uppercase"
          : "initial",
      };
    },
    resolvedJobDetails() {
      return this.module.config.jobDetails.options
        .filter((jobDetail) => {
          // Check if jobDetail is active
          if (!jobDetail.active) return false;

          // Use full_address instead of location when showFullAddress is true
          let key = jobDetail.key;
          if (
            key === "location" &&
            this.module.config.jobDetails.showFullAddress
          ) {
            key = "full_address";
          }

          // Check if value exists in jobData
          const value = this.jobData[key];

          // Return true if value exists and is not empty
          if (Array.isArray(value)) {
            return value.length > 0;
          }

          return value !== undefined && value !== null && value !== "";
        })
        .map((jobDetail) => {
          // Use full_address instead of location when showFullAddress is true
          const key =
            jobDetail.key === "location" &&
            this.module.config.jobDetails.showFullAddress
              ? "full_address"
              : jobDetail.key;

          return {
            ...jobDetail,
            value: this.jobData[key],
          };
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.hero-section {
  container-type: inline-size;
  contain: layout;
  display: flex;
  justify-content: center;

  background-color: var(--hero-background-color);
}

.inner-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  justify-content: center;

  max-width: 1600px;
  padding-block: 25px;
  padding-inline: var(--cr-spacing-l);

  @include mobile {
    padding-inline: var(--cr-spacing-xs);
  }
}

.media-box {
  aspect-ratio: 16 / 9;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
}

.titles {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  align-self: stretch;

  padding-top: 25px;
  padding-bottom: 35px;
}

.title {
  @include font-family("heading");
  @include scale-fonts("job-title");

  color: var(--hero-header-text-color);
  text-transform: var(--hero-header-capital-letters);
}

.sub-title {
  @include font-family("body");

  font-size: 14px;
  font-weight: var(--cr-body-font-weight);
  line-height: 21px;
  color: var(--hero-sub-title-text-color);
  text-transform: var(--hero-sub-title-capital-letters);
}

.job-details {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-content: center;
  align-items: center;
  align-self: stretch;

  padding-bottom: 50px;
}

.button-area {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
